import React, { useCallback, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  FormErrorMessage,
  SimpleGrid,
  Flex,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import MultiInputEntries from "components/multiInputEntries";
import { useParams } from "react-router-dom";
import { Post } from "api";
import { SortableConfig } from "views/admin/initialSetup";
import { useConfirmDialog } from "components/confirmDialog";

interface SortableSetup {
  appEvents: { eventName: string; eventTitle: string }[];
}

interface RoasGoal {
  dx: string;
  roasGoal: string;
}

interface FormData {
  sortableSetup: SortableSetup;
  roasIapShare: string;
  revenueEventName: string;
  isRevenueValueShared: boolean;
}

type AppDetailRouteParams = {
  appId: string;
  partnerId: string;
};

const SortableSetupForm: React.FC<{
  platformId: number;
  initialData?: SortableConfig[];
}> = ({ platformId, initialData }) => {
  const { ConfirmDialog, openDialog } = useConfirmDialog({
    title: "Confirm",
    description:
      "Are you sure you want to submit your results? This can't be undone.",
  });
  const toast = useToast();
  const params = useParams<AppDetailRouteParams>();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: initialData
      ? {
          isRevenueValueShared: initialData?.[0]?.isRevenueValueShared ?? false,
          revenueEventName: initialData?.[0]?.revenueEventName ?? "",
          roasIapShare: initialData?.[0]?.roasIapShare.toString() ?? "",
          sortableSetup: {
            appEvents: initialData?.[0]?.sortableSetup.appEvents ?? [],
          },
        }
      : {
          sortableSetup: {
            appEvents: [],
          },
          roasIapShare: "",
          revenueEventName: "",
          isRevenueValueShared: false,
        },
  });

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [roasGoals, setRoasGoals] = useState<RoasGoal[]>(
    initialData?.[0]?.roasGoals.map((goal) => ({
      dx: goal.dx.toString(),
      roasGoal: (goal.roasGoal * 100).toString(),
    })) ?? []
  );
  const [appEvents, setAppEvents] = useState<SortableSetup["appEvents"]>(
    initialData?.[0]?.sortableSetup.appEvents ?? []
  );

  const getIDs = (): Array<number | string | null> | null => {
    const parsedPartnerId = parseInt(params.partnerId, 10);
    const appId = params.appId;

    if (isNaN(parsedPartnerId) || !parsedPartnerId || !appId) {
      return null;
    }

    return [parsedPartnerId, appId];
  };

  const [partnerId, appId] = getIDs();

  const onSubmit = async (data: FormData) => {
    const result = await openDialog();
    if (!result) return;

    const body = {
      partnerId,
      platformId,
      appId,
      sortableSetup: {
        appEvents,
      },
      roasGoals: roasGoals.map((g) => ({
        dx: parseInt(g.dx),
        roasGoal: parseInt(g.roasGoal, 10) / 100,
      })),
      revenueEventName: data.revenueEventName ? data.revenueEventName : null,
      roasIapShare: data.roasIapShare ? parseFloat(data.roasIapShare) : null,
      isRevenueValueShared: data.isRevenueValueShared,
      isSubmission: true,
    };

    const URL = "/manage/partner/app/setup/sortable";
    setIsSubmitting(true);
    try {
      const response = await Post(URL, body);
      if (response.err) {
        toast({
          title: "Error",
          description: response.err,
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      toast({
        title: "Success",
        description: "Setup submitted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSave = async (data: FormData) => {
    const body = {
      partnerId,
      platformId,
      appId,
      sortableSetup: {
        appEvents,
      },
      roasGoals: roasGoals.map((g) => ({
        dx: parseInt(g.dx),
        roasGoal: parseInt(g.roasGoal, 10) / 100,
      })),
      revenueEventName: data.revenueEventName ? data.revenueEventName : null,
      roasIapShare: data.roasIapShare ? parseFloat(data.roasIapShare) : null,
      isRevenueValueShared: data.isRevenueValueShared,
      isSubmission: false,
    };

    const URL = "/manage/partner/app/setup/sortable";
    setIsSaving(true);
    try {
      const response = await Post(URL, body);
      if (response.err) {
        toast({
          title: "Error",
          description: response.err,
          status: "error",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      toast({
        title: "Success",
        description: "Setup saved successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const addNewGoalItem = useCallback(() => {
    setRoasGoals((_roasGoals) => [..._roasGoals, { dx: "", roasGoal: "" }]);
  }, []);

  const handleGoalsFieldChange = useCallback(
    (value: string, index: number, type: string) => {
      if (type === "dx" && parseInt(value, 10) < 0) return;

      if (type === "roasGoal" && parseInt(value, 10) >= 100) return;

      setRoasGoals((_roasGoals) => [
        ..._roasGoals.map((goal, idx) =>
          index === idx
            ? {
                dx: type === "dx" ? value : goal.dx,
                roasGoal: type === "roasGoal" ? value : goal.roasGoal,
              }
            : goal
        ),
      ]);
    },
    []
  );

  const handleGoalsRemove = useCallback((index: number) => {
    return setRoasGoals((prevItems) =>
      [...prevItems].filter((_, i) => i !== index)
    );
  }, []);

  const addNewAppEventItem = useCallback(() => {
    setAppEvents((_appEvents) => [
      ..._appEvents,
      { eventName: "", eventTitle: "" },
    ]);
  }, []);

  const handleAppEventsFieldChange = useCallback(
    (value: string, index: number, type: string) => {
      setAppEvents((_appEvents) => [
        ..._appEvents.map((event, idx) =>
          index === idx
            ? {
                eventName: type === "eventName" ? value : event.eventName,
                eventTitle: type === "eventTitle" ? value : event.eventTitle,
              }
            : event
        ),
      ]);
    },
    []
  );

  const handleAppEventsRemove = useCallback((index: number) => {
    return setAppEvents((prevItems) =>
      [...prevItems].filter((_, i) => i !== index)
    );
  }, []);

  return (
    <SimpleGrid
      columns={2}
      bgColor="white"
      borderRadius={"lg"}
      padding={8}
      gap={4}
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <Controller
        name="roasIapShare"
        control={control}
        render={({ field }) => (
          <FormControl isInvalid={!!errors.roasIapShare}>
            <FormLabel>ROAS IAP Share (%)</FormLabel>
            <Input
              {...field}
              type="number"
              placeholder="0"
              onBlur={(e) => {
                const value = parseFloat(e.target.value);
                if (value < 0 || value > 100) {
                  field.onChange("");
                }
              }}
            />
            <FormErrorMessage>{errors.roasIapShare?.message}</FormErrorMessage>
          </FormControl>
        )}
      />

      <Controller
        name="revenueEventName"
        control={control}
        render={({ field }) => (
          <FormControl isInvalid={!!errors.revenueEventName}>
            <FormLabel>Revenue Event Name</FormLabel>
            <Input {...field} placeholder="event_name" />
            <FormErrorMessage>
              {errors.revenueEventName?.message}
            </FormErrorMessage>
          </FormControl>
        )}
      />

      <Flex alignItems={"center"} gap={4} gridColumn="1 / -1">
        <Controller
          name="isRevenueValueShared"
          control={control}
          render={({ field: { onChange, value, ref } }) => (
            <Checkbox
              onChange={(e) => onChange(e.target.checked)}
              isChecked={value}
              ref={ref}
            >
              Is Revenue Value Shared
            </Checkbox>
          )}
        />
      </Flex>

      <Divider gridColumn="1 / -1" my={4} />

      <Flex gridColumn="1 / -1">
        <MultiInputEntries
          width={"95%"}
          sectionTitle="ROAS Goals"
          handleFieldChange={handleGoalsFieldChange}
          handleSave={() => {}}
          onRemove={handleGoalsRemove}
          addNewItem={addNewGoalItem}
          items={roasGoals}
          setItems={setRoasGoals}
          inputFields={[
            {
              title: "Day",
              leftAddon: "D",
              inputType: "numeric",
              min: 0,
              max: 365 * 2,
              keyName: "dx",
              placeHolder: "Day",
            },
            {
              title: "ROAS",
              leftAddon: "%",
              inputType: "numeric",
              min: 0,
              keyName: "roasGoal",
              placeHolder: "Goal",
            },
          ]}
          defaultEditableValue={false}
        />
      </Flex>
      <Divider gridColumn="1 / -1" my={4} />
      <Flex gridColumn="1 / -1">
        <MultiInputEntries
          width={"95%"}
          isReorderable
          sectionTitle="App Events (in linear order)"
          handleFieldChange={handleAppEventsFieldChange}
          handleSave={() => {}}
          onRemove={handleAppEventsRemove}
          addNewItem={addNewAppEventItem}
          items={appEvents}
          setItems={setAppEvents}
          inputFields={[
            {
              title: "Event Name",
              inputType: "text",
              keyName: "eventName",
              placeHolder: "Event name",
            },
            {
              title: "Event Title",
              inputType: "text",
              keyName: "eventTitle",
              placeHolder: "Event title",
            },
          ]}
          defaultEditableValue={false}
        />
      </Flex>

      <Flex
        mt={8}
        alignItems="center"
        justifyContent={"flex-end"}
        gridColumn="1 / -1"
      >
        <Button
          colorScheme="brand"
          variant={"ghost"}
          onClick={handleSubmit(onSave)}
          px={8}
          isLoading={isSaving}
          isDisabled={
            isSaving ||
            (roasGoals.length > 0 &&
              roasGoals.some((g) => !g.dx || !g.roasGoal)) ||
            appEvents.length === 0 ||
            appEvents.some((e) => !e.eventName || !e.eventTitle)
          }
        >
          Save
        </Button>
        <Button
          type="submit"
          colorScheme="brand"
          onClick={handleSubmit(onSubmit)}
          px={8}
          isLoading={isSubmitting}
          isDisabled={
            isSubmitting ||
            (roasGoals.length > 0 &&
              roasGoals.some((g) => !g.dx || !g.roasGoal)) ||
            appEvents.length === 0 ||
            appEvents.some((e) => !e.eventName || !e.eventTitle)
          }
        >
          Submit
        </Button>
      </Flex>
      {ConfirmDialog}
    </SimpleGrid>
  );
};

export default SortableSetupForm;
